import { baseLanguage, localizedText } from "../../components/Views/FastEval/utils/gridCategoryLabels";
import { getGoalRestriction, goalAppliesToAnyTaxon } from "../../utils/enrich/goalRestriction";

/**
 * Vue d'ensemble des tags et objectifs d'enrichissement (GitLab #1226) :
 * catégories → tags → objectifs (comportements attendus), tels que les parcs
 * les choisissent sur l'app et le web. Référentiel global (collections
 * `categories`, `tags`, `goals`) tenu par l'équipe AKONGO
 * (`/v2/akongo/settings/enrichment-tags`). Fonctions pures : l'écran, les
 * exports Excel et PDF et l'administration partagent le même modèle.
 */

export const TAG_ORIGINS = Object.freeze(["akongo", "park"]);
export const UNCATEGORIZED_ID = "__uncategorized__";
export const TAXON_FILTER_KINDS = Object.freeze(["species", "genus", "family"]);

const array = (value) => (Array.isArray(value) ? value : []);
const text = (value) => (value === null || value === undefined ? "" : String(value).trim());
const isActive = (document) => Boolean(document) && document.archived !== true;
const idOf = (document, ...keys) => text(keys.map((key) => document?.[key]).find((value) => text(value)));

/** Clé de recherche : sans accents ni casse (« Recherche » trouve « recherche alimentaire », « réflexion » trouve « reflexion »). */
export const searchKey = (value) => text(value).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/**
 * Nom d'une catégorie, d'un tag ou d'un objectif : langue, puis anglais, puis
 * français, puis toute autre langue (décision du 03/11/2026). Le nom saisi
 * (`name`, `label`, `tagName`) est le français : il tient lieu de `trad.fr`
 * quand celui-ci manque, pour qu'un parc français ne lise pas l'anglais.
 */
export const catalogueName = (document, language = "fr") => {
  const entered = text(document?.name) || text(document?.label) || text(document?.tagName);
  const trad = document?.trad && typeof document.trad === "object" ? document.trad : {};
  return localizedText({ ...trad, fr: text(trad.fr) || entered }, language) || entered;
};

/**
 * Origine d'un tag ou d'un objectif : « akongo » pour la liste commune, « park »
 * pour ce qu'un parc a proposé (`origin: "park"` ou `zooId` posé). Aujourd'hui
 * seule l'équipe AKONGO en crée ; la distinction est prête pour les objectifs
 * proposés par les parcs, appelés à devenir officiels.
 */
export const catalogueOrigin = (document) => (document?.origin === "park" || text(document?.zooId) ? "park" : "akongo");

const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : "");

/** Genre d'une espèce : premier mot de son nom latin (« Panthera leo » → « Panthera »). */
export const speciesGenus = (species) => capitalize(text(species?.specieLatinName).split(/\s+/)[0] || "");

const speciesName = (species, language) => {
  const common = language === "fr" ? text(species.specieName) : text(species.specieEnglishName) || text(species.specieName);
  const latin = text(species.specieLatinName);
  if (common && latin && searchKey(common) !== searchKey(latin)) return `${common} (${latin})`;
  return common || latin;
};

const familyName = (family, language) => {
  const common = language === "fr" ? text(family?.familyName) : text(family?.familyEnglishName) || text(family?.familyName);
  const latin = capitalize(text(family?.familyLatinName));
  if (common && latin && searchKey(common) !== searchKey(latin)) return `${common} (${latin})`;
  return common || latin;
};

const byLabel = (language) => (left, right) => left.label.localeCompare(right.label, language, { sensitivity: "base" });

/**
 * Index taxonomique tiré du référentiel des espèces (`species`) : options des
 * filtres Espèce, Genre et Famille, et taxons `{ specieId, familyId, orderId }`
 * de chacune pour la règle de restriction des objectifs (`goalRestriction`).
 */
export const buildTaxonomyIndex = (species = [], language = "fr") => {
  const lang = baseLanguage(language);
  const speciesOptions = [];
  const genera = new Map();
  const families = new Map();
  array(species).forEach((item) => {
    const value = idOf(item, "specieId", "id");
    if (!value) return;
    const label = speciesName(item, lang);
    if (!label) return;
    const familyId = idOf(item, "familyId") || idOf(item?.specieFamily, "familyId") || null;
    const orderId = idOf(item, "orderId") || idOf(item?.specieFamily?.familyOrder, "orderId") || null;
    const taxon = { specieId: value, familyId, orderId };
    const genus = speciesGenus(item);
    speciesOptions.push({ value, label, genus: genus || null, familyId, taxon });
    if (genus) {
      if (!genera.has(genus)) genera.set(genus, { value: genus, label: genus, taxa: [] });
      genera.get(genus).taxa.push(taxon);
    }
    if (familyId) {
      if (!families.has(familyId)) families.set(familyId, { value: familyId, label: familyName(item.specieFamily, lang) || familyId, taxa: [] });
      families.get(familyId).taxa.push(taxon);
    }
  });
  const sort = byLabel(lang);
  return Object.freeze({
    species: speciesOptions.sort(sort),
    genera: [...genera.values()].sort(sort),
    families: [...families.values()].sort(sort),
    speciesById: new Map(speciesOptions.map((option) => [option.value, option])),
    genusById: genera,
    familyById: families,
  });
};

/** Filtre taxonomique lu dans l'adresse : `species:<id>`, `genus:<nom>` ou `family:<id>`. */
export const parseTaxonFilter = (raw) => {
  const match = /^(species|genus|family):(.+)$/.exec(text(raw));
  return match ? { kind: match[1], value: match[2] } : null;
};
export const formatTaxonFilter = (filter) => (filter?.kind && filter?.value ? `${filter.kind}:${filter.value}` : null);

/**
 * Taxons d'un filtre (une espèce, ou toutes les espèces d'un genre ou d'une
 * famille). Une famille sans espèce connue garde un taxon de famille seule :
 * les objectifs restreints à la famille s'y appliquent encore. `null` : aucun filtre.
 */
export const taxaForFilter = (index, filter) => {
  if (!filter?.kind || !filter?.value) return null;
  if (filter.kind === "species") return [index?.speciesById?.get(filter.value)?.taxon || { specieId: filter.value, familyId: null, orderId: null }];
  if (filter.kind === "genus") return index?.genusById?.get(filter.value)?.taxa || [];
  if (filter.kind === "family") {
    const taxa = index?.familyById?.get(filter.value)?.taxa || [];
    return taxa.length ? taxa : [{ specieId: null, familyId: filter.value, orderId: null }];
  }
  return null;
};

/** Libellé du taxon filtré, pour la puce et l'en-tête des exports. */
export const taxonFilterLabel = (index, filter) => {
  if (!filter?.kind) return "";
  const source = filter.kind === "species" ? index?.speciesById : filter.kind === "genus" ? index?.genusById : index?.familyById;
  return source?.get(filter.value)?.label || filter.value;
};

const RESTRICTION_LISTS = Object.freeze({
  include: [["order", "orderWhiteList"], ["family", "famillyWhiteList"], ["species", "specieWhitelist"]],
  exclude: [["order", "orderBlackList"], ["family", "famillyBlackList"], ["species", "specieBlacklist"]],
});

/**
 * Espèces concernées par un objectif, à afficher : `all` (aucune
 * restriction), `only` (seulement ces taxons) ou `except` (tous sauf). Le nom
 * vient de l'index dans la langue de l'écran quand il est connu, sinon du
 * libellé enregistré avec la restriction.
 */
export const goalRestrictionSummary = (goal, index = null) => {
  const restriction = getGoalRestriction(goal);
  const lists = RESTRICTION_LISTS[restriction.mode === "include" ? "include" : "exclude"];
  const labels = lists.flatMap(([kind, field]) => array(goal?.[field]).map((entry) => {
    const value = entry && typeof entry === "object" ? text(entry.value) : text(entry);
    const known = kind === "species" ? index?.speciesById?.get(value) : kind === "family" ? index?.familyById?.get(value) : null;
    return known?.label || (entry && typeof entry === "object" ? text(entry.label) : "") || value;
  })).filter(Boolean);
  if (!labels.length) return { mode: "all", labels: [] };
  return { mode: restriction.mode === "include" ? "only" : "except", labels: [...new Set(labels)] };
};

/**
 * Arbre catégories → tags → objectifs, actifs seulement (`archived !== true`,
 * comme l'app mobile), triés par nom dans la langue demandée. Un tag dont la
 * catégorie a disparu va dans « Sans catégorie » ; un objectif sans tag connu
 * n'est pas proposé à la saisie et n'apparaît pas.
 */
export const buildTagCatalogue = ({ categories = [], tags = [], goals = [], language = "fr", index = null } = {}) => {
  const lang = baseLanguage(language);
  const compare = (left, right) => left.name.localeCompare(right.name, lang, { sensitivity: "base" });
  const activeCategories = array(categories).filter(isActive);
  const categoryById = new Map(activeCategories.map((category) => [idOf(category, "id", "categoryId"), category]));
  const goalsByTag = new Map();
  array(goals).filter(isActive).forEach((goal) => {
    const tagId = idOf(goal, "tagId");
    if (!tagId) return;
    if (!goalsByTag.has(tagId)) goalsByTag.set(tagId, []);
    goalsByTag.get(tagId).push({
      id: idOf(goal, "id", "goalId"),
      name: catalogueName(goal, lang),
      origin: catalogueOrigin(goal),
      restriction: goalRestrictionSummary(goal, index),
      source: goal,
    });
  });
  const tagsByCategory = new Map();
  array(tags).filter(isActive).forEach((tag) => {
    const id = idOf(tag, "id", "tagId");
    if (!id) return;
    const categoryId = categoryById.has(idOf(tag, "categoryId")) ? idOf(tag, "categoryId") : UNCATEGORIZED_ID;
    if (!tagsByCategory.has(categoryId)) tagsByCategory.set(categoryId, []);
    tagsByCategory.get(categoryId).push({
      id,
      name: catalogueName(tag, lang),
      origin: catalogueOrigin(tag),
      secondaryCategories: array(tag.altCategoryIds)
        .map((altId) => text(altId))
        .filter((altId) => altId && altId !== categoryId && categoryById.has(altId))
        .map((altId) => catalogueName(categoryById.get(altId), lang))
        .filter(Boolean),
      goals: (goalsByTag.get(id) || []).filter((goal) => goal.id && goal.name).sort(compare),
    });
  });
  const tree = activeCategories.map((category) => {
    const id = idOf(category, "id", "categoryId");
    return {
      id,
      name: catalogueName(category, lang),
      color: category.color || null,
      origin: catalogueOrigin(category),
      tags: (tagsByCategory.get(id) || []).filter((tag) => tag.name).sort(compare),
    };
  }).filter((category) => category.id && category.name).sort(compare);
  const orphans = (tagsByCategory.get(UNCATEGORIZED_ID) || []).filter((tag) => tag.name).sort(compare);
  return orphans.length ? [...tree, { id: UNCATEGORIZED_ID, name: null, color: null, origin: "akongo", tags: orphans }] : tree;
};

/**
 * Catalogue filtré : recherche sans accents sur les noms (une catégorie ou un
 * tag trouvé garde tout son contenu) et filtre taxonomique (seuls les
 * objectifs qui s'appliquent à au moins une espèce du filtre restent ; les
 * tags, communs à toutes les espèces, restent, avec `goalsHidden`).
 */
export const filterTagCatalogue = (catalogue, { search = "", taxa = null } = {}) => {
  const query = searchKey(search);
  const fits = (goal) => !taxa || goalAppliesToAnyTaxon(goal.source, taxa);
  return array(catalogue).map((category) => {
    const categoryMatch = !query || searchKey(category.name).includes(query);
    const tags = category.tags.map((tag) => {
      const tagMatch = categoryMatch || searchKey(tag.name).includes(query);
      const applicable = tag.goals.filter(fits);
      const goals = tagMatch ? applicable : applicable.filter((goal) => searchKey(goal.name).includes(query));
      if (!tagMatch && !goals.length) return null;
      return { ...tag, goals, goalsHidden: applicable.length < tag.goals.length };
    }).filter(Boolean);
    if (!tags.length && !categoryMatch) return null;
    return { ...category, tags };
  }).filter(Boolean);
};

/** Nombre de catégories, tags et objectifs d'un catalogue (filtré ou non). */
export const countTagCatalogue = (catalogue) => array(catalogue).reduce((counts, category) => ({
  categories: counts.categories + 1,
  tags: counts.tags + category.tags.length,
  goals: counts.goals + category.tags.reduce((total, tag) => total + tag.goals.length, 0),
}), { categories: 0, tags: 0, goals: 0 });

/** Identifiants affichés : l'export « liste affichée » reprend exactement ces lignes, dans sa propre langue. */
export const visibleCatalogueIds = (catalogue) => ({
  categoryIds: array(catalogue).map((category) => category.id),
  tagIds: array(catalogue).flatMap((category) => category.tags.map((tag) => tag.id)),
  goalIds: array(catalogue).flatMap((category) => category.tags.flatMap((tag) => tag.goals.map((goal) => goal.id))),
});

/** Restreint un catalogue aux identifiants d'un autre (même liste, autre langue). */
export const restrictTagCatalogue = (catalogue, visible) => {
  if (!visible) return array(catalogue);
  const categoryIds = new Set(visible.categoryIds);
  const tagIds = new Set(visible.tagIds);
  const goalIds = new Set(visible.goalIds);
  return array(catalogue)
    .filter((category) => categoryIds.has(category.id))
    .map((category) => ({
      ...category,
      tags: category.tags
        .filter((tag) => tagIds.has(tag.id))
        .map((tag) => ({ ...tag, goals: tag.goals.filter((goal) => goalIds.has(goal.id)) })),
    }));
};

/** « Seulement : Félins, Lion » / « Toutes sauf : Primates » / « Toutes les espèces ». */
export const restrictionText = (restriction, labels) => {
  if (!restriction || restriction.mode === "all") return labels.allSpecies;
  return labels[restriction.mode]({ list: restriction.labels.join(", ") });
};

/**
 * Lignes des exports : une par objectif, un tag sans objectif garde sa ligne.
 * `labels` : `{ uncategorized, allSpecies, only({list}), except({list}), origin: { akongo, park } }`.
 */
export const catalogueExportRows = (catalogue, labels) => array(catalogue).flatMap((category) => category.tags.flatMap((tag) => {
  const base = {
    categoryId: category.id,
    category: category.name || labels.uncategorized,
    categoryColor: category.color?.bg || null,
    tag: tag.name,
    otherCategories: tag.secondaryCategories.join(", "),
  };
  if (!tag.goals.length) return [{ ...base, goal: "", species: "", origin: labels.origin[tag.origin] || labels.origin.akongo }];
  return tag.goals.map((goal) => ({ ...base, goal: goal.name, species: restrictionText(goal.restriction, labels), origin: labels.origin[goal.origin] || labels.origin.akongo }));
}));
